<template>
  <div
    :class="{
      'bg-lighter': isGrayPage,
    }"
    class="default-vue"
  >
    <a href="#main-content" class="skip-link">{{ $t('wcag.skipNavigation') }}</a>
    <div role="alert" aria-live="assertive" aria-atomic="true" class="sr-only">{{ uiStore.tempErrorMsg || '' }}</div>
    <div role="status" aria-live="polite" aria-atomic="true" class="sr-only">{{ uiStore.tempMsg || '' }}</div>
    <transition name="fade">
      <LazyB2bB2cSelector />
    </transition>

    <transition name="fromLeft">
      <LazySideMenu v-if="showSideMenu" />
    </transition>
    <transition name="fromRight">
      <LazyMiniCart v-if="showMiniCart" />
    </transition>
    <transition name="fromRight">
      <LazyQuickBuy v-if="showQuickBuy" />
    </transition>
    <transition :name="isMobile ? 'fromBottom' : 'fromRight'">
      <LazyAddedToCart
        v-if="showAddedToCart"
        :is-scrolled="isScrolled"
        :scroll-pos="scrollPos"
      />
    </transition>
    <transition name="fromRight">
      <LazyDeclineOrderProposalModal v-if="showDeclineOrderProposalModal" />
    </transition>
    <transition name="fromRight">
      <LazyEditProductDiscountModal v-if="showEditProductDiscountModal" />
    </transition>
    <transition name="fromRight">
      <LazySigninModal v-if="showSigninModal" />
    </transition>
    <transition name="fromRight">
      <LazyImpersonateModal v-if="showImpersonateModal" />
    </transition>
    <transition name="fromRight">
      <LazyLogInAsCustomerModal v-if="showLogInAsCustomer" />
    </transition>
    <LazyCreatePayerModal v-if="showCreatePayerModal" />
    <transition name="fromRight">
      <LazyNotifyModal v-if="showNotify" />
    </transition>
    <transition name="fade">
      <LazyBarcodeScanner
        v-if="showScanner"
        @close="uiStore.setShowScanner(false)"
        @add-products="cartStore.addMultipleItemsToCart"
      />
    </transition>
    <transition name="fade">
      <div
        v-if="showModalBg"
        class="modalBg modalHeight"
        @click="uiStore.closeAllModal()"
      />
    </transition>
    <MarketSwitchModal v-if="showMarketSwitchModal" />
    <transition name="fadeSlowOut">
      <LazyTempError v-if="uiStore.tempErrorMsg" />
    </transition>
    <div
      v-if="!isCheckout && globalContentStore.getHasDifferentEndConsumerTypes"
      class="layout-container flex justify-between hidden"
    >
      <ConsumerType />
      <LangCurrencySelector />
    </div>
    <ImpersonatingNotice v-if="userStore.userProfile.impersonatingCompany" />
    <PointshopReminder v-else-if="userStore.isLoggedIn && pointShopReminder && !isCheckout" :to="pointShopReminder" />
    <SiteNotice v-else-if="!isCheckout" :settings="globalContentStore.siteNotice as StoryBlokSiteNoticeData" />
    <SurchargeNotice v-if="globalContentStore.showSurchargeNotice" />
    <CheckoutHeader v-if="isCheckout" />
    <PageHeader v-else />
    <DesktopMenu
      v-if="!isCheckout && !uiStore.isMobile"
      :key="`DesktopMenu${userStore.isLoggedIn ? 'loggedIn':''}`"
    />
    <MobileSearch v-if="!isCheckout" />
    <main id="main_content" class="main relative">
      <slot />
    </main>
    <transition name="fadeSlowOut">
      <LazyTempMsg v-if="uiStore.tempMsg" />
    </transition>

    <StickyBottomCompare />
    <PageFooter v-if="!isCheckout" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue';
import PageFooter from '~/components/body/PageFooter.vue';
import PageHeader from '~/components/body/PageHeader.vue';
import { storeToRefs } from 'pinia';
import { useUiStore } from '~/store/ui';
import { useGlobalContentStore } from '~/store/globalContent';
import MobileSearch from '~/components/search/MobileSearch.vue';
import SiteNotice from '~/components/body/SiteNotice.vue';
import ConsumerType from '~/components/body/ConsumerType.vue';
import ImpersonatingNotice from '~/components/body/ImpersonatingNotice.vue';
import SurchargeNotice from '~/components/body/SurchargeNotice.vue';
import DesktopMenu from '~/components/menu/DesktopMenu.vue';
import CheckoutHeader from '~/components/body/CheckoutHeader.vue';
import { useUserStore } from '~/store/user';
import { useCartStore } from '~/store/cart';
import type { StoryBlokSiteNoticeData } from '#constants/types/storyblok';
import useCheckScroll from '~/composables/useCheckScroll';
import PointshopReminder from '~/components/checkout/PointshopReminder.vue';
import LangCurrencySelector from '~/components/menu/LangCurrencySelector.vue';
import StickyBottomCompare from '~/components/compare/StickyBottomCompare.vue';
import MarketSwitchModal from '~/components/modals/MarketSwitchModal.vue';

const LazySideMenu = defineAsyncComponent(() => import('~/components/menu/SideMenu.vue'));
const LazyQuickBuy = defineAsyncComponent(() => import('~/components/product/QuickBuy.vue'));
const LazyMiniCart = defineAsyncComponent(() => import('~/components/cart/MiniCart.vue'));
const LazyAddedToCart = defineAsyncComponent(() => import('~/components/cart/AddedToCart.vue'));
const LazySigninModal = defineAsyncComponent(() => import('~/components/account/SigninModal.vue'));
const LazyImpersonateModal = defineAsyncComponent(() => import('~/components/account/ImpersonateModal.vue'));
const LazyLogInAsCustomerModal = defineAsyncComponent(() => import('~/components/account/LogInAsCustomerModal.vue'));
const LazyDeclineOrderProposalModal = defineAsyncComponent(() => import('~/components/account/DeclineOrderProposalModal.vue'));
const LazyEditProductDiscountModal = defineAsyncComponent(() => import('~/components/checkout/EditProductDiscountModal.vue'));
const LazyNotifyModal = defineAsyncComponent(() => import('~/components/product/NotifyModal.vue'));
const LazyCreatePayerModal = defineAsyncComponent(() => import('~/components/modals/CreatePayerModal.vue'));
const LazyTempError = defineAsyncComponent(() => import('~/components/body/TempError.vue'));
const LazyTempMsg = defineAsyncComponent(() => import('~/components/body/TempMsg.vue'));
const LazyBarcodeScanner = defineAsyncComponent(() => import('~/components/fastOrder/BarcodeScanner.vue'));
const LazyB2bB2cSelector = defineAsyncComponent(() => import('~/components/body/B2bB2cSelector.vue'));

const uiStore = useUiStore();
const userStore = useUserStore();
const cartStore = useCartStore();
const globalContentStore = useGlobalContentStore();
const { isScrolled, scrollPos } = useCheckScroll();

const { showScanner, showSigninModal, showSideMenu, showQuickBuy, showMiniCart, showModalBg, showAddedToCart, showNotify, isMobile, showDeclineOrderProposalModal, showEditProductDiscountModal, showImpersonateModal, showLogInAsCustomer, showCreatePayerModal, showMarketSwitchModal } = storeToRefs(uiStore);

const route = useRoute();
const isCheckout = ref(route.name?.toString().endsWith('checkout') || route.name?.toString().endsWith('checkoutv2') || route.name?.toString()?.endsWith('checkout-pointshop-basketId'));
const isGrayPage = ref(false);

const pointShopReminder = useCookie('pointShopReminder');

const checkGrayPage = () => {
  const name = route.name?.toString() ?? '';
  isGrayPage.value = (
    (name.includes('checkout') || name.includes('account') || name.includes('salestool'))
      && name !== 'lang-account-resetpassword-email-token'
      && name !== 'lang-account-signin'
      && name !== 'lang-account-invite'
  );
};
checkGrayPage();

// Creates a css variable for 100vh that works on ios safari
const setWindowHeight = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

// Detects mobile
const updateWidth = () => {
  uiStore.setIsMobile(window.innerWidth < 1024); //
  uiStore.setisTabletPortrait(window.innerWidth > 640 && window.innerWidth < 1024);
};

watch(showModalBg, (newVal)=> {
  if (newVal) {
    document.body.classList.add('noScroll');
  } else {
    document.body.classList.remove('noScroll');
  }
});
watch(() => route.name, (newVal)=> {
  isCheckout.value = (newVal?.toString()?.endsWith('checkout') || newVal?.toString()?.endsWith('checkoutv2') || route.name?.toString()?.endsWith('checkout-pointshop-basketId'));
  checkGrayPage();
});

const marketPopupDismissedCookie = useCookie('kahrs-market-popup-dismissed', {
  maxAge: 60 * 60 * 24 * 365,
  path: '/',
});

onMounted(()=> {
  setWindowHeight();
  window.addEventListener('resize', setWindowHeight);
  updateWidth();
  window.addEventListener('resize', updateWidth);

  // Show market switch popup for visitors whose detected country doesn't match the current market
  if (globalContentStore.getSuggestedMarket && !marketPopupDismissedCookie.value) {
    uiStore.setShowMarketSwitchModal(true);
  }

  // Settimeout needed here
  setTimeout(() => {
    /**
     * Load cart
     * If this is a checkout success page, pointshop or request proposal, that page will take care of cartloading
     */
    if (
      !((route.name === 'lang-checkout-result-cartId' && route.params.result === 'success') || route.name === 'lang-checkout-pointshow-basketId' || route.name === 'lang-b2b-request-proposal')
    ) {
      cartStore.getCart(false);
    }
    userStore.loadUserPrices();
  }, 200);
});
onUnmounted(() => {
  window.removeEventListener('resize', setWindowHeight);
  window.removeEventListener('resize', updateWidth);
  document.body.classList.remove('noScroll');
});

</script>

<style lang="postcss">
/**
Makes the footer just visible on empty page
 */
.main {
  min-height: calc(var(--vh, 1vh) * 100 - 275px);
}
</style>
